import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Cal, { getCalApi } from "@calcom/embed-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { BRAND } from "@/lib/brand";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MSG = 300;

const SECTOR_OPTIONS = [
    { v: "inmobiliario", l: "Inmobiliario" },
    { v: "servicios_profesionales", l: "Servicios profesionales (legal/fiscal/financiero)" },
    { v: "salud_bienestar", l: "Salud y bienestar prime" },
    { v: "financiero", l: "Financiero e intermediación" },
    { v: "hospitality", l: "Hospitality, eventos y restauración prime" },
    { v: "otro", l: "Otro (escribir)" },
];
const NIVEL_OPTIONS = [
    { v: "core", l: "Aurum Core — Para empezar" },
    { v: "pro", l: "Aurum Pro — Para escalar" },
    { v: "imperium", l: "Aurum Imperium — Para transformar" },
    { v: "no_seguro", l: "No estoy seguro aún" },
];
const VALID_NIVEL = new Set(["core", "pro", "imperium", "no_seguro"]);

export default function Contacto() {
    const [params] = useSearchParams();
    // `nivel` no se pregunta en el formulario por decisión de producto:
    // "después de conocerte y conocer tu negocio te diremos cuál es la solución".
    // Si llega por query (?nivel=…) lo aceptamos como contexto, sino enviamos default.
    const initialNivel = (() => {
        const n = params.get("nivel");
        return n && VALID_NIVEL.has(n) ? n : "no_seguro";
    })();

    const [form, setForm] = useState({
        nombre: "",
        email: "",
        empresa: "",
        sector: "",
        mensaje: "",
        preferencia_contacto: "email",
        privacidad_aceptada: false,
        website: "", // honeypot
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        document.title = "Contacto — Aurum Vanguard · Hablemos";
        window.scrollTo({ top: 0 });
    }, []);

    const update = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (form.nombre.trim().length < 2) e.nombre = "Introduce tu nombre completo.";
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
            e.email = "Introduce un email profesional válido.";
        if (!form.sector) e.sector = "Selecciona un sector.";
        if (form.mensaje.trim().length < 1) e.mensaje = "Cuéntanos brevemente tu situación.";
        if (form.mensaje.length > MAX_MSG)
            e.mensaje = `Máximo ${MAX_MSG} caracteres.`;
        if (!form.preferencia_contacto)
            e.preferencia_contacto = "Indica cómo prefieres que te contactemos.";
        if (!form.privacidad_aceptada)
            e.privacidad_aceptada = "Debes aceptar la política de privacidad.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = { ...form, nivel: initialNivel };
            await axios.post(`${API_BASE}/contact`, payload);
            setSuccess(true);
            toast.success("Hemos recibido tu solicitud. Te contactamos en menos de 4 horas hábiles.");
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "No se pudo enviar la solicitud. Inténtalo de nuevo en unos minutos.";
            toast.error(typeof detail === "string" ? detail : "No se pudo enviar la solicitud.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contacto-page" className="bg-white">
            <Navigation />

            <section className="pt-32 md:pt-44 pb-24 md:pb-32">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-5">
                            <h1
                                data-testid="contacto-headline"
                                className="font-serif-display italic text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1A1A1A]"
                            >
                                Hablemos.
                            </h1>
                            <p className="mt-6 font-serif-display italic text-lg md:text-xl text-[#666666] max-w-[420px]">
                                Te respondemos en menos de cuatro horas hábiles.
                            </p>

                            <div className="mt-14 space-y-8 text-sm">
                                <div>
                                    <p className="overline mb-2">Correo directo</p>
                                    <a
                                        href={`mailto:${BRAND.contact.email}`}
                                        data-testid="contacto-email-link"
                                        className="text-[#1A1A1A] hover:text-[#8B6914] transition-colors"
                                    >
                                        {BRAND.contact.email}
                                    </a>
                                </div>
                                <div>
                                    <p className="overline mb-2">Dónde estamos</p>
                                    <p className="text-[#666666]">
                                        Strategic Growth & Innovation Firm
                                        <br />
                                        Dubai, Emiratos Árabes Unidos
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            {success ? (
                                <ThankYou />
                            ) : (
                                <form
                                    data-testid="contact-form"
                                    onSubmit={onSubmit}
                                    noValidate
                                    className="bg-[#F4F1EA] border border-[#E5E0D5] p-8 md:p-12"
                                >
                                    {/* Honeypot — hidden field */}
                                    <input
                                        type="text"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.website}
                                        onChange={(e) => update("website", e.target.value)}
                                        className="absolute -left-[9999px] -top-[9999px]"
                                        aria-hidden="true"
                                    />

                                    <Field label="Nombre completo *" error={errors.nombre}>
                                        <Input
                                            data-testid="contact-input-nombre"
                                            value={form.nombre}
                                            onChange={(e) => update("nombre", e.target.value)}
                                            autoComplete="name"
                                            className="bg-white h-12 rounded-none border-[#E5E0D5] focus-visible:ring-0 focus-visible:border-[#8B6914]"
                                        />
                                    </Field>

                                    <Field label="Email profesional *" error={errors.email}>
                                        <Input
                                            data-testid="contact-input-email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            autoComplete="email"
                                            className="bg-white h-12 rounded-none border-[#E5E0D5] focus-visible:ring-0 focus-visible:border-[#8B6914]"
                                        />
                                    </Field>

                                    <Field label="Empresa o proyecto" error={errors.empresa}>
                                        <Input
                                            data-testid="contact-input-empresa"
                                            value={form.empresa}
                                            onChange={(e) => update("empresa", e.target.value)}
                                            autoComplete="organization"
                                            className="bg-white h-12 rounded-none border-[#E5E0D5] focus-visible:ring-0 focus-visible:border-[#8B6914]"
                                        />
                                    </Field>

                                    <Field label="Sector *" error={errors.sector}>
                                        <Select
                                            value={form.sector}
                                            onValueChange={(v) => update("sector", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="contact-select-sector"
                                                className="bg-white h-12 rounded-none border-[#E5E0D5] focus:ring-0 focus:border-[#8B6914]"
                                            >
                                                <SelectValue placeholder="Selecciona un sector" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SECTOR_OPTIONS.map((o) => (
                                                    <SelectItem
                                                        key={o.v}
                                                        value={o.v}
                                                        data-testid={`sector-option-${o.v}`}
                                                    >
                                                        {o.l}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>

                                    <Field
                                        label="Cuéntanos en una frase tu situación actual *"
                                        error={errors.mensaje}
                                        hint={`${form.mensaje.length}/${MAX_MSG}`}
                                    >
                                        <Textarea
                                            data-testid="contact-input-mensaje"
                                            value={form.mensaje}
                                            maxLength={MAX_MSG}
                                            onChange={(e) => update("mensaje", e.target.value)}
                                            rows={4}
                                            className="bg-white rounded-none border-[#E5E0D5] focus-visible:ring-0 focus-visible:border-[#8B6914] resize-none"
                                        />
                                    </Field>

                                    <Field
                                        label="Cómo prefieres que te contactemos *"
                                        error={errors.preferencia_contacto}
                                    >
                                        <RadioGroup
                                            value={form.preferencia_contacto}
                                            onValueChange={(v) => update("preferencia_contacto", v)}
                                            className="flex flex-col sm:flex-row gap-4 sm:gap-8"
                                        >
                                            {[
                                                { v: "email", l: "Email" },
                                                { v: "whatsapp", l: "WhatsApp" },
                                                { v: "llamada", l: "Llamada telefónica" },
                                            ].map((opt) => (
                                                <label
                                                    key={opt.v}
                                                    className="flex items-center gap-3 cursor-pointer text-sm text-[#1A1A1A]"
                                                >
                                                    <RadioGroupItem
                                                        value={opt.v}
                                                        data-testid={`contact-radio-${opt.v}`}
                                                        className="border-[#8B6914] text-[#8B6914]"
                                                    />
                                                    {opt.l}
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    </Field>

                                    <div className="mb-6 mt-2 flex items-start gap-3">
                                        <Checkbox
                                            id="privacidad"
                                            data-testid="contact-checkbox-privacidad"
                                            checked={form.privacidad_aceptada}
                                            onCheckedChange={(v) =>
                                                update("privacidad_aceptada", v === true)
                                            }
                                            className="mt-0.5 border-[#8B6914] data-[state=checked]:bg-[#8B6914] data-[state=checked]:text-white"
                                        />
                                        <label
                                            htmlFor="privacidad"
                                            className="text-xs text-[#666666] leading-[1.6] cursor-pointer"
                                        >
                                            He leído y acepto la{" "}
                                            <Link
                                                to="/privacidad"
                                                className="underline hover:text-[#8B6914]"
                                            >
                                                política de privacidad
                                            </Link>
                                            . *
                                        </label>
                                    </div>
                                    {errors.privacidad_aceptada && (
                                        <p className="-mt-4 mb-4 text-xs text-[#8B1A1A]">
                                            {errors.privacidad_aceptada}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="contact-submit"
                                        disabled={submitting}
                                        className="mt-2 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-9 py-4 bg-[#1A1A1A] text-white text-sm tracking-wide hover:bg-[#8B6914] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? "Enviando…" : "Enviar"}
                                        {!submitting && <ArrowRight size={16} />}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function Field({ label, error, hint, children }) {
    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
                <Label className="text-xs tracking-[0.18em] uppercase text-[#1A1A1A]">
                    {label}
                </Label>
                {hint && <span className="text-xs text-[#666666]">{hint}</span>}
            </div>
            {children}
            {error && (
                <p className="mt-2 text-xs text-[#8B1A1A]" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
}

function ThankYou() {
    const { t } = useTranslation();
    useEffect(() => {
        (async () => {
            try {
                const cal = await getCalApi({ namespace: "discovery" });
                cal("ui", {
                    theme: "light",
                    styles: { branding: { brandColor: "#8B6914" } },
                    hideEventTypeDetails: false,
                    layout: "month_view",
                });
            } catch (_) {}
        })();
    }, []);

    return (
        <div data-testid="contact-thank-you">
            <div className="bg-[#1A1A1A] text-[#F4F1EA] p-10 md:p-14 border border-[#8B6914]">
                <CheckCircle2 size={28} className="text-[#8B6914]" />
                <h2 className="font-serif-display italic mt-6 text-4xl md:text-5xl leading-tight text-[#F4F1EA]">
                    {t("thank_you.title")}
                </h2>
                <p className="mt-6 font-serif-display italic text-lg md:text-xl text-[#F4F1EA]">
                    {t("thank_you.subtitle")}
                </p>
                <p className="mt-6 text-[#A4A19A] leading-[1.7]">
                    {t("thank_you.calendar_intro")}
                </p>
            </div>

            <div
                data-testid="cal-inline-wrapper"
                className="mt-8 bg-white border border-[#E5E0D5] overflow-hidden"
                style={{ minHeight: 640 }}
            >
                <Cal
                    namespace="discovery"
                    calLink="aurumvanguardmeeting"
                    style={{ width: "100%", height: "640px", overflow: "scroll" }}
                    config={{ layout: "month_view", theme: "light" }}
                />
            </div>

            <a
                href="https://cal.com/aurumvanguardmeeting"
                target="_blank"
                rel="noreferrer"
                data-testid="thank-you-cal-link"
                className="mt-6 inline-flex items-center gap-3 text-sm tracking-wide text-[#8B6914] hover:text-[#A47F1E] transition-colors"
            >
                {t("thank_you.calendar_open_external")}
                <ArrowRight size={14} />
            </a>
        </div>
    );
}
